import { useEffect, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/EmptyState';
import { LevelPill } from '@/components/apps/LevelPill';
import { api } from '@/lib/api';
import { filterLogLines, filterLogs } from '@/lib/logFilter';
import { usePolledData, type PolledData } from '@/lib/hooks/usePolledData';
import type { AppContainerResponse } from '@/types/app';

export interface LogsTabProps {
  appId: number;
  appStatus: string;
}

const STRUCTURED = 'structured';
const POLL_MS = 3000;
const TAILS = [100, 500, 1000];

const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';
const checkLabelClass =
  'flex h-9 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 text-sm focus-within:ring-2 focus-within:ring-ring';
const scrollClass =
  'max-h-[60vh] overflow-auto rounded-md border bg-muted/40 outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface ContainerLogsResult {
  logs: string;
  empty: boolean;
  hint: string | null;
}

export function LogsTab({ appId, appStatus }: LogsTabProps) {
  const [selected, setSelected] = useState(STRUCTURED);
  const [tail, setTail] = useState(100);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState('');
  const [wrap, setWrap] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [containers, setContainers] = useState<AppContainerResponse[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.apps
      .containers(appId)
      .then((list) => {
        if (!cancelled) setContainers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Dropdown is optional; without containers only the structured log is offered.
      });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  // Fall back to the structured log when the selected container is no longer listed.
  const source = selected !== STRUCTURED && containers.some((c) => String(c.id) === selected) ? selected : STRUCTURED;
  const containerId = source === STRUCTURED ? null : Number(source);

  const changeSource = (next: string) => {
    setSelected(next);
    setQuery('');
    setLevel('');
    setAutoRefresh(false);
  };
  const key = `${source}:${tail}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Log source" value={source} onChange={(e) => changeSource(e.target.value)} className={selectClass}>
          <option value={STRUCTURED}>Application log (structured)</option>
          {containers.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name} ({c.status})
            </option>
          ))}
        </select>
        <select
          aria-label="Tail lines"
          value={String(tail)}
          onChange={(e) => setTail(Number(e.target.value))}
          className={selectClass}
        >
          {TAILS.map((n) => (
            <option key={n} value={String(n)}>
              Tail: {n} lines
            </option>
          ))}
        </select>
      </div>
      {containerId === null ? (
        <StructuredLogs
          key={key}
          appId={appId}
          tail={tail}
          polling={appStatus === 'pending'}
          level={level}
          setLevel={setLevel}
          query={query}
          setQuery={setQuery}
        />
      ) : (
        <ContainerLogs
          key={key}
          appId={appId}
          containerId={containerId}
          tail={tail}
          query={query}
          setQuery={setQuery}
          wrap={wrap}
          setWrap={setWrap}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
        />
      )}
    </div>
  );
}

function RefreshButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick}>
      <RefreshCw className="size-4" aria-hidden /> Refresh
    </Button>
  );
}

/** Shared loading / error / stale chrome; renders children once there is data. */
function LogsBody<T>({ poll, children }: { poll: PolledData<T>; children: (data: T) => ReactNode }) {
  const { data, error, loading, refetch } = poll;
  if (data === undefined) {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      );
    }
    return (
      <EmptyState
        as="h3"
        title="Could not load logs"
        description={error ?? undefined}
        action={
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry
          </Button>
        }
      />
    );
  }
  return (
    <>
      {error !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known logs — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}
      {children(data)}
    </>
  );
}

function StructuredLogs({
  appId,
  tail,
  polling,
  level,
  setLevel,
  query,
  setQuery,
}: {
  appId: number;
  tail: number;
  polling: boolean;
  level: string;
  setLevel: (v: string) => void;
  query: string;
  setQuery: (v: string) => void;
}) {
  const poll = usePolledData(() => api.apps.logs(appId, tail), polling ? POLL_MS : undefined);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Filter by level" value={level} onChange={(e) => setLevel(e.target.value)} className={selectClass}>
          <option value="">All levels</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
        <input
          type="search"
          aria-label="Search logs"
          placeholder="Search logs…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 min-w-40 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <RefreshButton onClick={poll.refetch} />
      </div>
      <LogsBody poll={poll}>
        {(data) => {
          const all = data.logs ?? [];
          const shown = filterLogs(all, { level, query });
          return (
            <>
              <p className="text-sm text-muted-foreground">
                {shown.length} of {all.length} entries
              </p>
              {shown.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No logs available</div>
              ) : (
                <div role="region" tabIndex={0} aria-label="Log output" className={`${scrollClass} space-y-1 p-2`}>
                  {shown.map((log, i) => {
                    const t = Date.parse(log.timestamp);
                    return (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="shrink-0 pt-0.5 font-mono text-xs text-muted-foreground">
                          {Number.isNaN(t) ? '—' : new Date(t).toLocaleString()}
                        </span>
                        <LevelPill level={log.level} />
                        <span className="min-w-0 flex-1 break-words">{log.message}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        }}
      </LogsBody>
    </div>
  );
}

function ContainerLogs({
  appId,
  containerId,
  tail,
  query,
  setQuery,
  wrap,
  setWrap,
  autoRefresh,
  setAutoRefresh,
}: {
  appId: number;
  containerId: number;
  tail: number;
  query: string;
  setQuery: (v: string) => void;
  wrap: boolean;
  setWrap: (v: boolean) => void;
  autoRefresh: boolean;
  setAutoRefresh: (v: boolean) => void;
}) {
  const poll = usePolledData<ContainerLogsResult>(
    () => api.apps.containerLogs(appId, containerId, tail),
    autoRefresh ? POLL_MS : undefined,
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          aria-label="Search logs"
          placeholder="Search logs…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 min-w-40 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <label className={checkLabelClass}>
          <input type="checkbox" checked={wrap} onChange={(e) => setWrap(e.target.checked)} />
          Wrap lines
        </label>
        <label className={checkLabelClass}>
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Auto refresh (3s)
        </label>
        <RefreshButton onClick={poll.refetch} />
      </div>
      <LogsBody poll={poll}>
        {(data) => {
          if (data.empty || !data.logs) {
            return (
              <div role="status" className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                <div className="font-medium text-foreground">No logs for this container</div>
                {data.hint && <div className="text-xs whitespace-pre-line break-words">{data.hint}</div>}
              </div>
            );
          }
          const all = filterLogLines(data.logs, '');
          const shown = filterLogLines(data.logs, query);
          return (
            <>
              <p className="text-sm text-muted-foreground">
                {shown.length} of {all.length} lines
              </p>
              <div role="region" tabIndex={0} aria-label="Log output" className={`${scrollClass} p-2`}>
                <pre
                  className={`font-mono text-xs ${wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'}`}
                >
                  {shown.join('\n')}
                </pre>
              </div>
            </>
          );
        }}
      </LogsBody>
    </div>
  );
}
